import React, { useRef, useState, useEffect } from 'react'
import './index.css'
import { Layout, Typography, Card, Alert, Input, Space, Table, Button, message } from 'antd'
import { DownOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import ExportJsonExcel from 'js-export-excel'
import Highlighter from 'react-highlight-words'
import axios from 'axios'

const items = [
  { key: '1', label: 'Action 1' },
  { key: '2', label: 'Action 2' },
]

const { Content } = Layout
const { Title, Paragraph } = Typography

// 独立的 ExpandedRow 组件
const ExpandedRow = ({ record }) => {
  const [expandedData, setExpandedData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchExpandedData = async () => {
      setLoading(true)
      try {
        const response = await axios.get('/api/Data/getExpandedData', {
          params: { BioProject: record.bioProject },
        })
        const processedData = response.data.map((item, index) => ({
          key: index.toString(),
          ...item,
        }))
        setExpandedData(processedData)
      } catch (err) {
        console.error('Error fetching expanded data:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchExpandedData()
  }, [record])

  const columns = [
    { title: 'Run', dataIndex: 'run', key: 'run' },
    { title: 'PaperName', dataIndex: 'paperName', key: 'paperName' },
    { title: 'Project Description', dataIndex: 'projectedDescription', key: 'projectedDescription', ellipsis: true },
    { title: 'BioSample', dataIndex: 'bioSample', key: 'bioSample' },
    { title: 'Instrument', dataIndex: 'instrument', key: 'instrument'},
    { title: 'Organism', dataIndex: 'organism', key: 'organism'},
    { title: 'ReleaseDate', dataIndex: 'releaseDate', key: 'releaseDate'},
  ]

  return (
    <div style={{ width: '100%', overflowX: 'auto', padding: '8px 0' }}>
      <Table
        columns={columns}
        dataSource={expandedData}
        pagination={false}
        loading={loading}
        // scroll={{ x: 'max-content' }}
        locale={{ emptyText: error ? `Error: ${error}` : 'No data' }}
      />
    </div>
  )
}

// Data 组件
const Data = () => {
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef(null)


    // 新增：分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15, // 默认每页15条
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm({ closeDropdown: true })
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters, confirm) => {
    clearFilters()
    setSearchText('')
    confirm({ closeDropdown: true })
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => clearFilters && handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => {
      if (!record[dataIndex]) return false;
      return record[dataIndex].toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => {
          if (searchInput.current) {
            searchInput.current.select();
          }
        }, 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />
      ) : (
        text
    ),
  })

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'bioProject',
      key: 'bioProject',
      width: 160,
      ellipsis: true,
      ...getColumnSearchProps('bioProject'),
    },
    {
      title: 'Curated Projects',
      dataIndex: 'curatedProjects',
      key: 'curatedProjects',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Whether Correct',
      dataIndex: 'whetherCorrect',
      key: 'whetherCorrect',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Total Runs',
      dataIndex: 'totalRuns',
      key: 'totalRuns',
      width: 120,
      ellipsis: true,
    },
  ]

  const [processedData, setProcessedData] = useState([])
  const [loadingData, setLoadingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true)
        const response = await axios.get('/api/Data/getColumnsData')
        const processedData = response.data.map(item => ({
          key: item.columns.bioProject,
          ...item.columns,
          totalRuns: item.totalRuns,
        }))
        setProcessedData(processedData)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoadingData(false)
      }
    }
    fetchData()
  }, [])

  const [counts, setCounts] = useState({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchQuantity = async () => {
      try {
        setLoading(true)
        const res = await axios.get('/api/getDataCount')
        if (Array.isArray(res.data.quantity) && res.data.quantity.length >= 6) {
          setCounts({
            q1: res.data.quantity[0],
            q2: res.data.quantity[1],
            q3: res.data.quantity[2],
            q4: 388, //res.data.quantity[3],
            q5: res.data.quantity[4],
            q6: res.data.quantity[5],
          })
        } else {
          setError('Invalid data format')
        }
      } catch (err) {
        setError('Load failed')
      } finally {
        setLoading(false)
      }
    }
    fetchQuantity()
  }, [])

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px 16px', maxWidth: 1400, margin: '0 auto' }}>

        {/* 顶部标题 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px 20px' }}>
          <Title level={2} style={{ margin: 0, color: '#002156', fontSize: 22 }}>
            Projects and runs
          </Title>
        </Card>

        {/* 数据统计卡片 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 14, lineHeight: 1.7 }}>
            <strong>Data summary:</strong>
            <ul style={{ paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
              <li>
                <strong>Meta data</strong> for in total <span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q1}</span> projects, containing <span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q2}</span> runs (samples) are available in
                <strong style={{ marginLeft: 4 }}>
                  <span style={{ color: 'black' }}>LR</span>
                  <span style={{ color: 'black' }}>M</span>
                  <span style={{ color: 'black' }}>repo</span>
                </strong>.
                These runs include:
                <ul style={{ paddingLeft: 20, marginTop: 4 }}>
                  <li><span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q3}</span> Oxford Nanopore Technologies</li>
                  <li><span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q4}</span> Pacific Biosciences</li>
                </ul>
              </li>
              {/* <li>
                Among which, <strong>raw data</strong> processed: <span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q3}</span> runs, belonging to <span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q5}</span> projects.
              </li>
              <li>
                Among which, <strong>microbe abundance data</strong> available: <span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q4}</span> runs, belonging to <span style={{ color: '#1677ff', fontWeight: 'bold' }}>{loading ? 'Loading...' : error ? error : counts.q6}</span> projects.
              </li> */}
            </ul>
          </div>
        </Card>

        {/* Projects 表格 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: 16, fontSize: 16, fontWeight: 600, color: '#1f2937', borderLeft: '3px solid #1677ff', paddingLeft: 10 }}>
            Projects
          </div>
          {/* <Table
            columns={columns}
            expandable={{ expandedRowRender: (record) => <ExpandedRow record={record} /> }}
            dataSource={processedData}
            scroll={{ x: 'max-content' }}
            rowKey="key"
            pagination={{ pageSize: 15, showTotal: (total) => `Total ${total} entries` }}
            loading={loadingData}
            bordered={false}
          /> */}
          <Table
            columns={columns}
            expandable={{ expandedRowRender: (record) => <ExpandedRow record={record} /> }}
            dataSource={processedData}
            scroll={{ x: 'max-content' }}
            rowKey="key"
            // ✅ 修复：页码 + 每页条数 都能正常切换
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total) => `Total ${total} entries`,
              pageSizeOptions: ['10', '15', '30', '50', '100'],
              // 👇 新增：切换页码（第 2 页、第 3 页...）
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize: pageSize });
              },
              // 👇 切换每页条数
              onShowSizeChange: (current, size) => {
                setPagination({ current: 1, pageSize: size });
              },
            }}
            loading={loadingData}
            bordered={false}
          />
        </Card>

        {/* 提示信息 */}
        <Alert
          message={<strong>Raw data processed*</strong>}
          description={
            <>
              In <strong>LRMrepo</strong>, project information is also available for <span style={{ color: '#1677ff', fontWeight: 'bold' }}>76</span> unprocessed projects (total <span style={{ color: '#1677ff', fontWeight: 'bold' }}>31,381</span> runs), mostly due to lack of phenotype information.
            </>
          }
          type="info"
          showIcon
          style={{ marginBottom: 20, borderRadius: 8 }}
        />

        {/* Runs 标题 */}
        {/* <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: 0, fontSize: 16, fontWeight: 600, color: '#1f2937', borderLeft: '3px solid #1677ff', paddingLeft: 10 }}>
            Runs
          </div>
        </Card> */}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={async () => {
            message.loading('Fetching all data...', 0)
            try {
              if (!processedData.length) { message.destroy(); message.warning('No data to export'); return }
              const exportData = processedData.map(d => ({
                'BioProject': d.bioProject || '',
                'Curated Projects': d.curatedProjects || '',
                'Whether Correct': d.whetherCorrect || '',
                'Total Runs': d.totalRuns || '',
              }))
              const option = { fileName: 'LRMrepo_Data_all', datas: [{ sheetData: exportData, sheetName: 'Data', sheetHeader: Object.keys(exportData[0]) }] }
              new ExportJsonExcel(option).saveExcel()
              message.destroy()
              message.success('Export successfully')
            } catch (err) { message.destroy(); message.error('Export failed') }
          }}>Export All to Excel</Button>
        </div>

        {/* QC 提示
        <Alert
          message={<strong>* QC Status:</strong>}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>1: QC ok & data loaded</li>
              <li>0: QC NOT ok & data not loaded</li>
              <li>(Empty): Data yet to be loaded</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 40, borderRadius: 8 }}
        /> */}

      </Content>
    </Layout>
  )
}

export default Data